import { CrmTableDropdown } from "components/CrmTableDropdown/CrmTableDropdown";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import {
  Profile,
  useAssignSalesPersonMutation,
  useGetAllPreGroupProfilesQuery,
} from "store/apis/salesCrmProfile";
import { debounce } from "utils/debouncing";
import AddCrmEntityTableModal from "./AddCrmEntityTableModal";
import NewGroupCreationModal from "./NewGroupEntityModal";
import { ApproveModal, RejectModal } from "./StatusModal";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { Agents } from "types/Models/agents";
import { MultiValue, SingleValue } from "react-select";
import {
  CellContext,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { TableUI } from "components/design/TableUI";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { formatDate, formatTime } from "utils/formatDate";
import { SolidCross } from "components/icons/SolidCross";
import { SolidCheck } from "components/icons/SolidCheck";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Search } from "components/DesignSystem/Search/Search";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Cross } from "components/icons/Cross";
import { CheckOutline } from "components/icons/CheckOutline";
import ToolTip from "components/design/toolTip";

const SalesPersonDropdown = ({
  agent,
  profileId,
}: {
  agent: Agents | null;
  profileId: string | undefined;
}) => {
  const { isAdmin } = useRoleBasedView();
  const { alertToast, successToast } = useToast();
  const [assignSalesPerson, { isLoading: isSalesPersonLoading }] =
    useAssignSalesPersonMutation();

  const onAgentChange = async (
    values: SingleValue<OptionData> | MultiValue<OptionData>
  ) => {
    if (values && !(values instanceof Array) && profileId) {
      const { value: agentProfileId } = values;
      try {
        await assignSalesPerson({
          profileId,
          agent_profile_id: agentProfileId,
        }).unwrap();
        successToast({ message: "Salesperson has been assigned" });
      } catch (error: any) {
        alertToast({ message: error?.data?.error?.message });
      }
    }
  };

  const { data: agentOptions = [] } = useGetAssignAgentsQuery(
    { agentType: "SALES_EXECUTIVE" },
    { skip: !isAdmin }
  );

  const selectedSalesPerson = agentOptions.find(
    (agentOption) => agentOption.profile_id == agent?.profile_id
  );

  return (
    <>
      <Combobox
        size="small"
        components={{
          ClearIndicator: () => null,
          LoadingIndicator: () => <Loader size="small" />,
        }}
        menuPortalTarget={document.body}
        onChange={onAgentChange}
        options={agentOptions.map((agent) => ({
          label: agent.name,
          value: agent.profile_id,
        }))}
        value={
          selectedSalesPerson
            ? {
                label: selectedSalesPerson.name,
                value: selectedSalesPerson.profile_id,
              }
            : null
        }
        isLoading={isSalesPersonLoading}
      />
    </>
  );
};

const SalesCrmProfiles = () => {
  const { update } = useUpdateQuery();
  const query = useQuery();
  const searchTerm = query.get("search_term") || null;
  const [pageNumber, setPageNumber] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveUserProfileId, setApproveUserProfileId] = useState("");
  const [showGroupCreationModal, setShowGroupCreationModal] = useState(false);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [userDataPayload, setUserDataPayLoad] = useState<any>();
  const [groupName, setGroupName] = useState<string>("");

  const {
    data: preGroupProfiles,
    isSuccess,
    isFetching,
  } = useGetAllPreGroupProfilesQuery({
    pageNumber,
    searchTerm,
  });

  const { profiles, total_pages } = preGroupProfiles || {};

  const search = debounce((e: any) => {
    const { value } = e.target;
    if (value) {
      update({ query: "search_term", value: value });
    } else {
      update({ query: "search_term", value: null });
    }
  });

  const approveHandler = (profileId: string) => {
    setShowModal(true);
    setApproveUserProfileId(profileId);
  };

  const rejectHandler = (profileId: string) => {
    setShowRejectModal(true);
    setApproveUserProfileId(profileId);
  };

  const newGroupCreationHandler = ({
    first_name,
    last_name,
    email,
    linkedin,
  }: {
    first_name: string;
    last_name: string;
    email: string;
    linkedin?: string;
  }) => {
    setUserDataPayLoad({
      first_name,
      last_name,
      email,
      linkedin,
    });
    setShowGroupCreationModal(true);
  };

  const createColumn = createColumnHelper<Profile>();
  const columns = useMemo(
    () => [
      createColumn.accessor("first_name", {
        header: "Name",
        size: 14,
        cell: ({ row }: any) => (
          <ToolTip
            text={`${row.original?.first_name} ${row.original?.last_name}`}
          >
            <div className="t-truncate">
              {row.original?.first_name} {row.original?.last_name}
            </div>
          </ToolTip>
        ),
      }),
      createColumn.accessor("email", {
        header: "Email",
        size: 16,
        cell: (info) => (
          <ToolTip text={info.getValue()}>
            <div className="t-truncate">{info.getValue()}</div>
          </ToolTip>
        ),
      }),
      createColumn.accessor("is_email_verified", {
        header: () => <div className="t-text-center">Email OTP</div>,
        size: 8,
        cell: (info) => (
          <div className="t-flex t-justify-center">
            {info.getValue() ? (
              <span className="t-flex t-text-red-70">
                <SolidCheck size="24" />
              </span>
            ) : (
              <SolidCross size="24" />
            )}
          </div>
        ),
      }),
      createColumn.accessor("is_mobile_verified", {
        header: () => <div className="t-text-center">Mobile OTP</div>,
        size: 8,
        cell: (info) => (
          <div className="t-flex t-justify-center">
            {info.getValue() ? (
              <span className="t-flex t-text-red-70">
                <SolidCheck size="24" />
              </span>
            ) : (
              <SolidCross size="24" />
            )}
          </div>
        ),
      }),
      createColumn.accessor("salesperson", {
        header: "Sales person",
        size: 14,
        cell: (info: CellContext<Profile, Agents>) => {
          return (
            <SalesPersonDropdown
              agent={info.row.original.salesperson}
              profileId={info.row.original.uuid}
            />
          );
        },
      }),
      createColumn.accessor("is_restricted", {
        header: "Status",
        size: 11,
        cell: ({ row }: any) => {
          if (
            !row.original?.is_email_verified &&
            !row.original?.is_mobile_verified
          ) {
            return <div>-</div>;
          }
          if (row.original?.is_rejected) {
            return <Tag tagType="red">Rejected</Tag>;
          }
          if (row.original?.is_restricted) {
            return (
              <ConditionalToolTip
                condition={
                  !row.original?.salesperson &&
                  "Please assign a salesperson first"
                }
              >
                <div className="t-flex t-gap-2">
                  <Button
                    size="small"
                    customType="icon"
                    onClick={() => approveHandler(row.original?.uuid)}
                    disabled={!row.original.salesperson}
                  >
                    <CheckOutline />
                  </Button>
                  <Button
                    size="small"
                    customType="icon"
                    onClick={() => {
                      rejectHandler(row.original?.uuid);
                    }}
                    disabled={!row.original.salesperson}
                  >
                    <Cross color="currentColor" />
                  </Button>
                </div>
              </ConditionalToolTip>
            );
          }
          if (!row.original?.is_restricted && !row.original?.is_rejected) {
            return <Tag tagType="green">Approved</Tag>;
          }
        },
      }),
      createColumn.accessor("group", {
        header: "Group",
        size: 12,
        cell: ({ row }: any) => {
          const { first_name, last_name, email, linkedin } = row.original;
          const groupName = row.original?.group?.name;
          const isSingleWord = !groupName?.includes(" ");

          return (
            <>
              {groupName ? (
                <ToolTip text={groupName}>
                  <div
                    className={isSingleWord ? "t-truncate" : "t-line-clamp-2"}
                  >
                    {groupName}
                  </div>
                </ToolTip>
              ) : (
                <div>
                  {row.original?.is_rejected ? (
                    "-"
                  ) : (
                    <Button
                      customType="primary_outlined"
                      size="small"
                      onClick={() =>
                        newGroupCreationHandler({
                          first_name,
                          last_name,
                          email,
                          linkedin,
                        })
                      }
                    >
                      New group
                    </Button>
                  )}
                </div>
              )}
            </>
          );
        },
      }),
      createColumn.accessor("created_at", {
        header: "Time",
        size: 11,
        cell: (info) => (
          <div>
            <div className="t-text-text-60 t-text-body-sm">
              {formatDate(info.getValue())}
            </div>
            <div className="t-text-body-sm t-text-text-30">
              {formatTime(info.getValue())}
            </div>
          </div>
        ),
      }),
      createColumn.accessor("uuid", {
        header: "",
        id: "threedots",
        size: 5,
        cell: ({ row }: any) => {
          const { uuid } = row.original;
          return <CrmTableDropdown actionOn="profile" uuid={uuid} />;
        },
      }),
    ],
    []
  );

  const data = useMemo(() => profiles || [], [profiles]);

  const table = useReactTable({
    data: data || [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <>
      <div className="t-w-full t-h-full t-overflow-hidden t-flex t-flex-col">
        <div className="t-flex t-pl-0 t-pr-5 t-gap-5 t-justify-between t-shrink-0">
          <div className="t-w-1/3">
            <Search
              defaultValue={searchTerm || ""}
              onChange={search}
              placeholder="Search user"
              block
            />
          </div>

          <PaginationHandler
            page_num={pageNumber}
            per_page={preGroupProfiles?.per_page || 1}
            total_count={preGroupProfiles?.total_count || 0}
            total_pages={total_pages || 1}
            onPageNumChange={setPageNumber}
            isLoading={isFetching}
          />
        </div>

        <div className="t-flex t-mb-3 t-justify-end"></div>

        {isFetching && !isSuccess ? (
          <div className="t-w-full">
            <Loader />
          </div>
        ) : (
          <div className="t-flex-1 t-min-h-0 t-flex t-flex-col t-overflow-hidden">
            <ApproveModal
              showModal={showModal}
              setShowModal={setShowModal}
              approveUserProfileId={approveUserProfileId}
            />
            <RejectModal
              showRejectModal={showRejectModal}
              setShowRejectModal={setShowRejectModal}
              approveUserProfileId={approveUserProfileId}
            />
            <div className="t-flex-1 t-min-h-0 t-overflow-auto">
              <div className="t-h-full sm:t-min-w-[900px]">
                <TableUI table={table} layout="fixed" height="100%" />
              </div>
            </div>
            <NewGroupCreationModal
              showModal={showGroupCreationModal}
              setShowModal={setShowGroupCreationModal}
              setShowEntityModal={setShowEntityModal}
              setUserDataPayLoad={setUserDataPayLoad}
              userDataPayload={userDataPayload}
              setGroupName={setGroupName}
              fromProfiles
            />
            <AddCrmEntityTableModal
              setShowGroupModal={setShowGroupCreationModal}
              showModal={showEntityModal}
              setModalShow={setShowEntityModal}
              userDataPayload={userDataPayload}
              groupName={groupName}
            />

            <div className="t-flex t-p-4 t-justify-end t-shrink-0">
              <PaginationHandler
                page_num={pageNumber}
                per_page={preGroupProfiles?.per_page || 1}
                total_count={preGroupProfiles?.total_count || 0}
                total_pages={total_pages || 1}
                onPageNumChange={setPageNumber}
                isLoading={isFetching}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SalesCrmProfiles;
